/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useLocation } from "react-router-dom"

/* Helpers, constants, custom methods, internal libraries */
import { PEOPLE_ROUTE, PEOPLE_TEAMS_ROUTE, PEOPLE_REMOVED_ROUTE, PEOPLE_GUEST_ROUTE } from "../../constants/routes";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePeopleService } from "../../services/peopleService";
import { useUserService } from "../../services/userService";
import { useHasPermission } from '../../hooks/useHasPermission';
import { commasFormat } from '../../helpers/general';
import { useTeamService } from '../../services/teamService';
import { useGuestProfileService } from '../../services/guestProfileService';
import { AIRLINES } from "../../constants/appConstants";

/* Components */
import PeopleTab from "./peopleTab";
import RemovedTab from "./removedTab";
import TeamsTab from "./teamsTab";
import GuestTab from "./guestTab";
import UCSelect from "../ucselect";

/* Styles */
import styles, {tabsSelectOpen} from "./styles.js"

const PeopleTabbedNav = ({getTeamsList, teamsResponse, deleteTeam, guestProfiles, getGuestProfilesAction}) => {
  const location = useLocation();
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [defaultPage, setDefaultPage] = useState("people");
  const [totalTabs, setTotalTabs] = useState({
    activeCount: "",
    removedCount: "",
    teamCount: "",
    guestCount: "",
  });
  const { getUsersCount } = usePeopleService();
  const { getTeamsCount } = useTeamService({skipUseEffect: true});
  const { getGuestProfilesCount } = useGuestProfileService();
  const { userDetails } = useUserService();
  const { checkPermission } = useHasPermission();

  const canShowGuestTab = checkPermission("F_GUEST_PROFILES_VIEW") && [AIRLINES.AS, AIRLINES.AC].includes(userDetails?.userInfo?.providerInfo?.providerCode);
  useEffect(() => {
    setDefaultPage(getDefaultPage())
  }, [location.pathname]);

  useEffect(() => {
    const fetchCounts = async () => {
      const activeCount = await getUsersCount("ACTIVE");
      const removedCount = await getUsersCount("REMOVED");
      const teamCount = await getTeamsCount();
      const guestCount = checkPermission("F_GUEST_PROFILES_VIEW") ? await getGuestProfilesCount() : "";

      setTotalTabs(prev => ({
        ...prev,
        activeCount: activeCount || 0,
        removedCount: removedCount || 0,
        teamCount: teamCount || 0,
        guestCount: guestCount || 0,
      }));
    };

    fetchCounts();
  }, []);


  useEffect(() => {
    if (guestProfiles?.results?.resultTotal !== undefined) {
      setTotalTabs(prev => ({ ...prev, guestCount: guestProfiles.results.resultTotal }));
    }
  }, [guestProfiles]);

  const getDefaultPage = () => {
    if(location.pathname.includes(PEOPLE_REMOVED_ROUTE)) return "removed"
    if(location.pathname.includes(PEOPLE_TEAMS_ROUTE)) return "teams"
    if(location.pathname.includes(PEOPLE_ROUTE)) return "people"
    if(location.pathname.includes(PEOPLE_GUEST_ROUTE)) return "guest"
  }

  const getDefaultLabel = () => {
    if(location.pathname.includes(PEOPLE_REMOVED_ROUTE)) return <FormattedMessage id="people.removed"/>
    if(location.pathname.includes(PEOPLE_TEAMS_ROUTE)) return <FormattedMessage id="people.teams"/>
    if(location.pathname.includes(PEOPLE_GUEST_ROUTE)) return <FormattedMessage id="people.guest"/>
    if(location.pathname.includes(PEOPLE_ROUTE)) return getPeopleTabName()
  }

  const handleTabChange = nextTab => {
    switch(nextTab) {      
      case "people":
        navigate('../' + PEOPLE_ROUTE)
      break;
      case "removed":
        navigate('../' + PEOPLE_REMOVED_ROUTE)
      break;
      case "teams":
        navigate('../' + PEOPLE_TEAMS_ROUTE);
      break;
      case "guest":
        navigate('../' + PEOPLE_GUEST_ROUTE);
      break;
    }
  }

  const viewTab = tab => {
    switch(tab) {
      case "people":
        return <PeopleTab />
      case "removed":
        return <RemovedTab />
      case "teams":
        return <TeamsTab getTeamsList={getTeamsList} teamsResponse={teamsResponse} deleteTeam={deleteTeam}/>
      case "guest":
        return <GuestTab guestProfiles={guestProfiles} getGuestProfilesAction={getGuestProfilesAction}/>
    }
  }

  //todo: move to helper
  const getPeopleTabName = () => {
    switch (userDetails?.userInfo?.providerInfo?.providerCode) {
      case AIRLINES.AA:
        return <FormattedMessage id="people.active" />
      case AIRLINES.UC:
        return <FormattedMessage id="people.UCtitle" />
      case AIRLINES.AS:
        return <FormattedMessage id="people.AStitle" />
      case AIRLINES.AM:
        return <FormattedMessage id="people.AMtitle" />
      case AIRLINES.CHOICE:
        return <FormattedMessage id="people.CHOICEtitle" />
      case AIRLINES.AC:
        return <FormattedMessage id="people.ACtitle" />
    }
  }

  const tabOptions = [
    { value: 'people', label: getPeopleTabName() },
    ...(totalTabs?.removedCount ? [{ value: 'removed', label: <FormattedMessage id="people.removed" /> }] : []),
    { value: 'teams', label: <FormattedMessage id="people.teams" /> },
    ...(canShowGuestTab ? [{value: 'guest', label: <FormattedMessage id="people.guest" /> }] : []),
  ]


  return (
    <div className={classes.peopleTabbedNav}>
     {isMobile ? (
        <>
          <UCSelect
            styles={{ ...tabsSelectOpen }}
            defaultValue={{ 
              value: getDefaultPage() || defaultPage,
              label: getDefaultLabel()
            }}
            options={tabOptions}
            onChange={(tab) => handleTabChange(tab.value)}
          />
          {viewTab(getDefaultPage() || defaultPage)}
        </>
      ) : (
        <Tabs
          onSelect={(nextTab) => handleTabChange(nextTab)}
          defaultActiveKey={getDefaultPage()}
          activeKey={defaultPage}
          id="people-page-tabs"
          className={classes.tabs}
        >
          <Tab
            eventKey="people"
            title={
              <>
                {getPeopleTabName()}
                {(totalTabs?.activeCount || totalTabs?.activeCount === 0) &&
                  <p className={classes.totalPeople}>
                    {commasFormat(totalTabs?.activeCount?.toString())}
                  </p>
                }
              </>
            }
          >
            {getDefaultPage() === 'people' && <PeopleTab />}
          </Tab>
          {totalTabs?.removedCount > 0 && (
            <Tab
              eventKey="removed"
              title={
                <>
                  <FormattedMessage id="people.removed" />
                  {(totalTabs?.removedCount || totalTabs?.removedCount === 0) &&
                    <p className={classes.totalPeople}>
                      {commasFormat(totalTabs?.removedCount?.toString())}
                    </p>
                  }
                </>
              }
            >
              {getDefaultPage() === 'removed' && <RemovedTab />}
            </Tab>
          )}
          <Tab
            eventKey="teams"
            title={
              <>
                <FormattedMessage id="people.teams" />
                {(totalTabs?.teamCount || totalTabs?.teamCount === 0) &&
                  <p className={classes.totalPeople}>
                    {commasFormat(totalTabs?.teamCount?.toString())}
                  </p>
                }
              </>
            }
          >
            {getDefaultPage() === 'teams' && (
              <TeamsTab
                getTeamsList={getTeamsList}
                teamsResponse={teamsResponse}
                deleteTeam={deleteTeam}
              />
            )}
          </Tab>
          {canShowGuestTab && (
            <Tab eventKey="guest" title={
              <>
                <FormattedMessage id="people.guest" />
                {(totalTabs?.guestCount || totalTabs?.guestCount === 0) &&
                  <p className={classes.totalPeople}>
                    {commasFormat(totalTabs?.guestCount?.toString())}
                  </p>
                }
              </>
            }>
              {getDefaultPage() === 'guest' && (
                <GuestTab guestProfiles={guestProfiles} getGuestProfilesAction={getGuestProfilesAction} />
              )}
            </Tab>
          )}
        </Tabs>
      )}
    </div>
  );
}

export default PeopleTabbedNav;
